/**
 * The review step's summary and itemised order (session W4). Split from
 * wc-brief.js, which theme check limits to 60 KB as a first-load script;
 * each function takes the <wc-brief> element.
 */

export function renderSummary(el) {
  const state = el.readState();
  const d = el.data;
  const core = el.core;
  const put = (key, text) => {
    const node = /** @type {HTMLElement | null} */ (el.querySelector(`[data-wc-summary="${key}"]`));
    if (node) node.textContent = text;
  };
  const people = core.clampInt(state.people, 0, core.LIMITS.people);
  const pets = core.clampInt(state.pets, 0, core.LIMITS.pets);
  const who = [
    people ? d.labels.peopleCount.replace("%n%", String(people)) : "",
    pets ? d.labels.petsCount.replace("%n%", String(pets)) : "",
  ].filter(Boolean);
  const subjects = core.subjectLabels(state, d);
  const firstName = subjects[0] && !/^(Person|Pet) \d+$/.test(subjects[0]) ? subjects[0] : "";
  const lead = state.memorial
    ? firstName
      ? d.labels.inMemory.replace("%name%", firstName)
      : d.labels.inMemoryPlain
    : who.join(", ") || d.labels.nobody;
  put(
    "who",
    [lead, state.recipient && `${d.labels.forWord} ${state.recipient}`, state.occasion].filter(Boolean).join(" · "),
  );
  renderLines(el, state);
  if (subjects.length) put("subjects", subjects.join(", "));

  const valueName = (pos) => d.options[pos].values.find((v) => String(v.id) === String(el.options[pos]))?.name || "";
  const artist = (d.artists || []).find((a) => a.handle === state.artist);
  put(
    "style",
    [
      valueName(d.positions.style),
      artist ? d.labels.yourArtist.replace("%name%", artist.firstName) : d.labels.nextAvailable,
    ].join(" · "),
  );
  const frameValue = d.options[d.positions.frame].values.find(
    (v) => String(v.id) === String(el.options[d.positions.frame]),
  );
  const finishWord =
    { framed: d.labels.finishFramed, print: d.labels.finishPrint, canvas: d.labels.finishCanvas }[frameValue?.finish] ||
    "";
  // In the words the size step used ("Framed, Matte Black", "Print only"),
  // never the store's option name ("Poster Only").
  const finishText = frameValue?.finish === "framed" ? `${finishWord}, ${frameValue.name}` : finishWord;
  put("size", [core.formatSize(valueName(d.positions.size), d.withCm), finishText].filter(Boolean).join(" · "));
  put(
    "photos",
    state.photos === "later"
      ? d.labels.photosLater
      : state.photoCount
        ? d.labels.photosCount.replace("%n%", String(state.photoCount))
        : d.labels.photosNow,
  );
  const note = /** @type {HTMLElement | null} */ (el.querySelector('[data-wc-summary="notes"]'));
  if (note) {
    note.textContent = state.notes.trim();
    note.closest("[data-wc-summary-row]")?.toggleAttribute("hidden", !state.notes.trim());
    // Long notes are shown in the body face with only... the whole note is the
    // customer's own words; beyond ~25 words the hand accent stops (brand §3.4).
    note.toggleAttribute("data-long", state.notes.trim().split(/\s+/).length > 25);
  }
  const text = core.textToPrint(state);
  put("text", text);
  el.querySelector('[data-wc-summary="text"]')?.closest("[data-wc-summary-row]")?.toggleAttribute("hidden", !text);
}

/**
 * The order, line by line, above "Due today": what each charge is for,
 * before the shopper is asked to pay for it.
 * @param {any} state
 */
function renderLines(el, state) {
  const list = el.querySelector("[data-wc-lines]");
  if (!list) return;
  const d = el.data;
  const words = {
    portrait: d.productTitle,
    extra_person: d.labels.lineExtraPerson,
    extra_pet: d.labels.lineExtraPet,
    angel_wings: d.labels.lineAngelWings,
    extra_priority: d.labels.extraPriority,
    extra_digital: d.labels.extraDigital,
    extra_protection: d.labels.extraProtection,
  };
  const lines = el.core.buildLines(state, d);
  const row = (label, cents, className) => {
    const li = document.createElement("li");
    if (className) li.className = className;
    const name = document.createElement("span");
    name.textContent = label;
    const amount = document.createElement("span");
    amount.textContent = el.money(cents);
    li.append(name, amount);
    return li;
  };
  list.replaceChildren(
    ...lines.map((l) => row(`${words[l.role] || ""}${l.quantity > 1 ? ` × ${l.quantity}` : ""}`, l.price * l.quantity)),
    row(
      d.labels.total,
      lines.reduce((sum, l) => sum + l.price * l.quantity, 0),
      "wc-brief__lines-total",
    ),
  );
}
